<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section
      class="relative min-h-[50svh] md:h-[490px] pb-4 pt-20 md:pb-20 md:pt-40 flex flex-col"
    >
      <!-- Background Image with Overlay -->
      <div class="absolute inset-0 pointer-events-none">
        <img
          src="/images/agent-hero.jpeg"
          alt="Hero Background"
          class="w-full h-full object-cover object-[75%_50%] sm:object-center"
        />
        <div class="absolute inset-0 gradient-overlay" />
      </div>

      <!-- Hero Content -->
      <div
        class="relative max-w-[1080px] mx-auto px-4 md:px-6 w-full mt-auto md:mt-0 md:my-auto"
      >
        <div class="flex flex-col gap-6 md:gap-12">
          <div
            class="flex flex-col gap-2 text-white max-w-full md:max-w-[648px]"
          >
            <h1
              class="text-3xl md:text-display-lg font-semibold leading-tight tracking-wide"
            >
              {{ $t('verified_agents_page.hero.title') }}
            </h1>
            <p
              class="text-md font-semibold md:text-2xl tracking-wider leading-loose max-w-full md:max-w-[480px] whitespace-pre-line"
            >
              {{ $t('verified_agents_page.hero.subtitle') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Agents Section -->
    <section class="bg-pruta-green pt-10 pb-8">
      <div class="max-w-7xl mx-auto px-4 space-y-8 ">
        <!-- Section Header -->
        <div class="flex items-end justify-center">
          <h2
            class="text-lg md:text-2xl text-center font-medium text-brand "
          >
            {{ $t('verified_agents_page.browse_text') }}
          </h2>
        </div>

        <!-- Search/Filter Toolbar -->
        <div class="border border-pruta-green-300 rounded-lg px-4 py-4 mx-auto max-w-5xl">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Search by Agent Name -->
            <div class="flex flex-col gap-2">
              <label class="text-sm md:text-base font-medium text-brand"
                >{{ $t('verified_agents_page.search.agent_name') }}</label
              >
              <div class="relative">
                <input
                  v-model="filters.agentName"
                  @input="debouncedSearch"
                  type="text"
                  :placeholder="$t('verified_agents_page.search.placeholder_all')"
                  class="w-full border border-light-grey rounded-lg px-3.5 py-2 text-sm md:text-base text-dark-grey focus:outline-none focus:border-pruta-green"
                />
                <svg
                  class="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Search by Suburb -->
            <div class="flex flex-col gap-2">
              <label class="text-sm md:text-base font-medium text-brand"
                >{{ $t('verified_agents_page.search.suburb') }}</label
              >
              <div class="relative">
                <input
                  v-model="filters.suburb"
                  @input="debouncedSearch"
                  type="text"
                  :placeholder="$t('verified_agents_page.search.placeholder_all')"
                  class="w-full border border-light-grey rounded-lg px-3.5 py-2 text-sm md:text-base text-dark-grey focus:outline-none focus:border-pruta-green"
                />
                <svg
                  class="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Search by Language -->
            <div class="flex flex-col gap-2">
              <label class="text-sm md:text-base font-medium text-brand"
                >{{ $t('verified_agents_page.search.language') }}</label
              >
              <div class="relative">
                <input
                  v-model="filters.language"
                  @input="debouncedSearch"
                  type="text"
                  :placeholder="$t('verified_agents_page.search.placeholder_all')"
                  class="w-full border border-light-grey rounded-lg px-3.5 py-2 text-sm md:text-base text-dark-grey focus:outline-none focus:border-pruta-green"
                />
                <svg
                  class="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="bg-gray-lightest pb-20">
      <div class="max-w-7xl mx-auto ">
        <!-- Agent Cards Grid -->
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-8 px-4 py-6"
        >
          <AgentCard v-for="agent in agents" :key="agent.id" :agent="agent" />
        </div>

        <!-- Pagination Controls -->
        <div
          v-if="totalPages > 1"
          class="flex justify-center items-center gap-2 md:gap-4 mt-8"
        >
          <button
            @click="prevPage"
            :disabled="page === 1"
            class="px-3 md:px-4 py-2 border border-gray-300 rounded-md text-sm md:text-base text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {{ $t('verified_agents_page.pagination.previous') }}
          </button>
          <span class="text-sm md:text-base text-gray-600">
            {{ $t('verified_agents_page.pagination.page_x_of_y', { page: page, totalPages: totalPages }) }}
          </span>
          <button
            @click="nextPage"
            :disabled="page === totalPages"
            class="px-3 md:px-4 py-2 border border-gray-300 rounded-md text-sm md:text-base text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {{ $t('verified_agents_page.pagination.next') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

// Search filters
const filters = ref({
  agentName: "",
  suburb: "",
  language: "",
});

// Sample agent data (replace with real data from API)
// Fetch agents from Supabase
// Pagination state
const page = ref(1);
const limit = ref(12);

// Fetch agents from Server API with filters and pagination
const { data, refresh } = await useFetch("/api/agents", {
  query: computed(() => ({
    page: page.value,
    limit: limit.value,
    name: filters.value.agentName,
    suburb: filters.value.suburb,
    language: filters.value.language,
  })),
  watch: [page], // Only watch page changes, filters are handled by debounced search
});

const agents = computed(() => data.value?.agents || []);
const totalAgents = computed(() => data.value?.total || 0);
const totalPages = computed(() => Math.ceil(totalAgents.value / limit.value));

// Debounce search input to avoid too many requests
let debounceTimer;
const debouncedSearch = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    page.value = 1; // Reset to first page on new search
    refresh();
  }, 500);
};

// Pagination handlers
const nextPage = () => {
  if (page.value < totalPages.value) {
    page.value++;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const prevPage = () => {
  if (page.value > 1) {
    page.value--;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};
</script>

<style scoped>
/* Mobile gradient - 0deg for both gradients (default) */
.gradient-overlay {
  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 43.807%,
      rgba(0, 0, 0, 0.3) 51.584%,
      rgba(0, 0, 0, 0.6) 100%
    ),
    linear-gradient(
      180deg,
      rgba(119, 135, 0, 0.15) 0%,
      rgba(119, 135, 0, 0.15) 100%
    );
}

/* Desktop gradient - original degrees (180deg) at md breakpoint */
@media (min-width: 768px) {
  .gradient-overlay {
    background: linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 43.807%,
        rgba(0, 0, 0, 0.3) 51.584%,
        rgba(0, 0, 0, 0.6) 100%
      ),
      linear-gradient(
        90deg,
        rgba(119, 135, 0, 0.15) 0%,
        rgba(119, 135, 0, 0.15) 100%
      );
  }
}
</style>
